// src/pages/Platform/numberOwnershipRules.ts — the decisions the
// number-ownership screen has to get right, kept out of the component so they
// can be tested.
//
// Named *Rules and not numberOwnership.ts because macOS resolves imports
// case-insensitively and webpack tries .ts before .tsx: a sibling module named
// numberOwnership.ts silently satisfies `import NumberOwnership from
// './NumberOwnership'`, and the page fails to build with "does not contain a
// default export" pointing at a file that plainly has one.
//
// Three of these exist because the API is asymmetric in ways a reader would not
// guess:
//
//   1. `provider_numbers` carries BOTH `business_id` (the switch's routing
//      label) and `purchased_by_organization_id` (the owner). They are set by
//      different endpoints and routinely disagree: `acquire` writes the owner
//      and never touches `business_id`, so a number moved to a new organisation
//      still reads "POC" on the switch. Showing `business_id` as the holder —
//      which is what /telephony/providers does today — names the wrong tenant.
//
//   2. Every timestamp on this payload arrives as epoch MILLIS, a JSON number,
//      not the ISO string the neighbouring types declare. `new Date("17858…")`
//      is Invalid Date, so one accidental String() renders every date blank.
//
//   3. `acquire` refuses for two different reasons, and only one of them is
//      force-able. Offering "force" for the other is offering a button that
//      cannot work.
import type { ProviderNumber } from 'api/services/providers.service';
import type { NumberAllocation } from 'api/services/numberOwnership.service';

// ── time ────────────────────────────────────────────────────────────────────

/**
 * A wire timestamp as a Date.
 *
 * cpass serialises LocalDateTime as epoch millis (a number). Some sibling
 * endpoints send ISO strings. Both are accepted; anything unparseable becomes
 * null rather than an Invalid Date that renders as "Invalid Date" on screen.
 */
export const asDate = (v: unknown): Date | null => {
  if (v === null || v === undefined || v === '') return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  if (typeof v === 'number') return isNaN(v) ? null : new Date(v);
  if (typeof v === 'string') {
    // A numeric string is still epoch millis — Date can't read those.
    const n = /^\d{10,}$/.test(v.trim()) ? Number(v.trim()) : NaN;
    const d = isNaN(n) ? new Date(v) : new Date(n);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

/** A wire timestamp for display, or an em dash. Never "Invalid Date". */
export const formatWhen = (v: unknown): string => {
  const d = asDate(v);
  return d ? d.toLocaleString() : '—';
};

/** Whole days from `now` until `v`; negative once past. null if undated. */
export const daysUntil = (v: unknown, now: Date = new Date()): number | null => {
  const d = asDate(v);
  if (!d) return null;
  return Math.ceil((d.getTime() - now.getTime()) / 86_400_000);
};

// ── lock-in ─────────────────────────────────────────────────────────────────

/**
 * Whether the holder is still inside their committed term.
 *
 * The server computes this into `in_lock_in`, so that is trusted first; the
 * date comparison is the fallback for payloads that predate the field. Reading
 * only the date would drift from the server's own answer, and the server's is
 * the one that decides whether the mutation is refused.
 */
export const lockedIn = (n: Partial<ProviderNumber> | null | undefined, now: Date = new Date()): boolean => {
  if (!n) return false;
  if (typeof n.in_lock_in === 'boolean') return n.in_lock_in;
  const ends = asDate(n.lock_in_ends_at);
  return !!ends && ends.getTime() > now.getTime();
};

/** Whether this number currently has an owner at all. */
export const isHeld = (n: Partial<ProviderNumber> | null | undefined): boolean =>
  !!(n && n.purchased_by_organization_id && String(n.purchased_by_organization_id).trim());

export type AcquireBlock = 'none' | 'already-holder' | 'lock-in';

/**
 * Why acquire would be refused, if it would.
 *
 * NumberOwnershipService.acquire raises for two cases and they are not the
 * same: re-assigning to the organisation that already holds the number is
 * refused unconditionally — `force` is checked only on the lock-in branch, so a
 * forced re-assign to the same org still fails. The screen must therefore
 * disable the action rather than offer an override that cannot work.
 */
export const acquireBlock = (
  n: Partial<ProviderNumber> | null | undefined,
  targetOrganizationId: string,
  now: Date = new Date(),
): AcquireBlock => {
  if (!n || !targetOrganizationId) return 'none';
  if (isHeld(n) && n.purchased_by_organization_id === targetOrganizationId) return 'already-holder';
  if (isHeld(n) && lockedIn(n, now)) return 'lock-in';
  return 'none';
};

/** Whether `force=true` is what stands between this acquire and success. */
export const acquireNeedsForce = (
  n: Partial<ProviderNumber> | null | undefined,
  targetOrganizationId: string,
  now: Date = new Date(),
): boolean => acquireBlock(n, targetOrganizationId, now) === 'lock-in';

/** Release is refused inside the term, and force is the only way past it. */
export const releaseNeedsForce = (
  n: Partial<ProviderNumber> | null | undefined,
  now: Date = new Date(),
): boolean => lockedIn(n, now);

// ── holder identity ─────────────────────────────────────────────────────────

/** The little an organisation needs to be recognised here. */
export interface KnownOrg {
  name: string;
  /** The switch-side code, which is what `provider_numbers.business_id` holds. */
  businessId?: string | null;
}

export type OrgLookup = Map<string, KnownOrg> | Record<string, KnownOrg>;

const lookup = (orgs: OrgLookup, id: string): KnownOrg | undefined =>
  orgs instanceof Map ? orgs.get(id) : orgs[id];

export interface Holder {
  /** The owning organisation id, or null while the number sits in the pool. */
  organizationId: string | null;
  /** What to print. Never a bare UUID when a name is known. */
  label: string;
  /** False when only the id is known — the screen says so rather than implying a name. */
  named: boolean;
  /**
   * `business_id`: the switch's routing label. Shown as a separate, clearly
   * labelled hint, because it is NOT the owner and drifts after every acquire.
   */
  switchLabel: string | null;
  /**
   * Whether the routing label still agrees with the owner.
   *
   * Compared against the organisation's `businessId` — its switch-side code —
   * NOT against its display name. "CMC" and "CatchMyCall" are the same tenant,
   * and comparing the strings would cry mismatch on every correctly-labelled
   * number on the platform. null when the owner could not be resolved, or when
   * there is no label to compare.
   */
  switchLabelMatchesOwner: boolean | null;
}

/** The current holder of a number, resolved against whatever we could load. */
export const holderOf = (
  n: Partial<ProviderNumber> | null | undefined,
  orgs: OrgLookup = {},
): Holder => {
  const switchLabel = (n?.business_id && String(n.business_id).trim()) || null;
  if (!isHeld(n)) {
    return { organizationId: null, label: 'In pool', named: true, switchLabel, switchLabelMatchesOwner: null };
  }
  const id = String(n!.purchased_by_organization_id);
  const org = lookup(orgs, id);
  const code = org?.businessId ? String(org.businessId).trim() : '';
  const matches = !switchLabel || !code ? null : code.toLowerCase() === switchLabel.toLowerCase();
  return org
    ? { organizationId: id, label: org.name, named: true, switchLabel, switchLabelMatchesOwner: matches }
    : { organizationId: id, label: shortId(id), named: false, switchLabel, switchLabelMatchesOwner: null };
};

/** A UUID trimmed to something readable in a table cell. */
export const shortId = (id: string): string => (id.length > 12 ? `${id.slice(0, 8)}…` : id);

// ── searching and filtering ─────────────────────────────────────────────────

/**
 * The digits of a dialable number, with the international prefix removed.
 *
 * The same DID is written "+912265197330" and "00919228034177" in this
 * inventory, so a literal substring match makes the search box fail on exactly
 * the number the operator pasted from the other screen.
 */
export const dialDigits = (v: unknown): string => {
  const digits = String(v ?? '').replace(/\D/g, '');
  return digits.startsWith('00') ? digits.slice(2) : digits;
};

/** Whether a row answers a free-text query over its number, label or switch id. */
export const matchesQuery = (n: Partial<ProviderNumber>, query: string): boolean => {
  const q = (query || '').trim();
  if (!q) return true;
  const qd = dialDigits(q);
  if (qd && dialDigits(n.e164).includes(qd)) return true;
  const lower = q.toLowerCase();
  return [n.e164, n.label, n.business_id, n.purchased_by_organization_id]
    .some(v => typeof v === 'string' && v.toLowerCase().includes(lower));
};

export type HolderFilter = 'all' | 'held' | 'pool' | 'locked';

export interface NumberFilters {
  query?: string;
  organizationId?: string;
  providerId?: number | '';
  holder?: HolderFilter;
  /** RETIRED numbers are garbage and hidden unless asked for. */
  showRetired?: boolean;
}

/** The rows a set of filters leaves, in a stable order (by number). */
export const filterNumbers = (
  rows: ProviderNumber[],
  f: NumberFilters,
  now: Date = new Date(),
): ProviderNumber[] =>
  (rows || [])
    .filter(n => f.showRetired || n.status !== 'RETIRED')
    .filter(n => matchesQuery(n, f.query || ''))
    .filter(n => !f.organizationId || n.purchased_by_organization_id === f.organizationId)
    .filter(n => f.providerId === undefined || f.providerId === '' || n.provider_id === f.providerId)
    .filter(n => {
      switch (f.holder) {
        case 'held': return isHeld(n);
        case 'pool': return !isHeld(n);
        case 'locked': return isHeld(n) && lockedIn(n, now);
        default: return true;
      }
    })
    .slice()
    .sort((a, b) => dialDigits(a.e164).localeCompare(dialDigits(b.e164)));

// ── history ─────────────────────────────────────────────────────────────────

/**
 * The tenure that is still open, if any.
 *
 * NumberAllocation arrives in camelCase while the number row beside it is
 * snake_case — same feature, two conventions — and it carries both an `open`
 * flag and a null `releasedAt`. `releasedAt` is the column the server queries
 * on, so it decides here too.
 */
export const openTenure = (history: NumberAllocation[] | null | undefined): NumberAllocation | null =>
  (history || []).find(a => a && a.releasedAt === null) || null;

/** History newest-first, so a drawer reads top-down as "now, then before". */
export const byMostRecent = (history: NumberAllocation[] | null | undefined): NumberAllocation[] =>
  (history || []).slice().sort((a, b) => (asDate(b?.acquiredAt)?.getTime() || 0) - (asDate(a?.acquiredAt)?.getTime() || 0));

/** How many rows in the inventory name this organisation as their owner. */
export const heldCountInInventory = (rows: ProviderNumber[] | null | undefined, organizationId: string): number =>
  (rows || []).filter(n => !!organizationId && n.purchased_by_organization_id === organizationId).length;

/**
 * What an organisation holds, according to each of the two records.
 *
 * `provider_numbers.purchased_by_organization_id` is the current-owner column;
 * `number_allocations` with a null `released_at` is the open tenure. They are
 * written together and should agree — when they do not, one of them is going to
 * answer a question wrongly, and the acquire about to be made is a good moment
 * to notice. Returns null when the tenures have not been loaded, so the caller
 * can stay silent rather than report a disagreement with nothing.
 */
export const holdingsAgree = (
  inventoryCount: number,
  tenures: NumberAllocation[] | null | undefined,
): boolean | null => (tenures ? tenures.length === inventoryCount : null);
